import { useMemo } from 'react'
import * as z from 'zod'

import { ValuePeriod } from '#domain/types'
import type { Section, Subtopic, Topic } from '#survey'

import type { SubmissionAnswers, SubtopicRecord } from '..'
import { buildDefaultValue } from './defaultValues'
import { getPeriodAnswerKey } from './periodKey'
import { buildAnswerSchema } from './schemas'

export { getPeriodAnswerKey, PERIOD_KEY_SUFFIX } from './periodKey'

type InitialValues = Record<string, unknown>

export const buildDefaultValuesAndSchema = (
  sections: Section[],
  initialValues: InitialValues = {}
): {
  defaultValues: InitialValues
  schema: z.ZodObject
} => {
  const questions = sections.flatMap(section => section.questions)

  const defaultValues: InitialValues = { ...initialValues }

  const shape: Record<string, z.ZodTypeAny> = {}

  questions.forEach(question => {
    defaultValues[question.id] ??= buildDefaultValue(question)
    shape[question.id] = buildAnswerSchema(question)

    // A moneyWithPeriod question owns a second answer key, `<id>Period`, holding
    // the chosen unit. defaultPeriod is always set (schema `.default`), so it
    // seeds the menu and persists with new/edited submissions.
    if (question.type === 'moneyWithPeriod') {
      const periodKey = getPeriodAnswerKey(question.id)
      defaultValues[periodKey] ??= question.defaultPeriod
      shape[periodKey] = z.enum(ValuePeriod)
    }
  })

  // we use partial schema because some answers can be optional due to invisible question.
  const schema = z.object(shape).partial()

  return { defaultValues, schema }
}

export const useDefaultValuesAndSchema = (
  sections: Section[],
  submissionDoc?: { entity: { answers: SubmissionAnswers } }
): {
  defaultValues: InitialValues
  schema: z.ZodObject
} => {
  const initialValues = submissionDoc?.entity.answers

  return useMemo(
    () => buildDefaultValuesAndSchema(sections, initialValues),
    [sections, initialValues]
  )
}

export const pickSubtopicSections = (
  topic: Topic,
  subtopicId: string
): Section[] | undefined =>
  topic.subtopics.find(subtopic => subtopic.id === subtopicId)?.sections

export const pickMissingSubtopics = (
  topic: Topic,
  subtopicRecords: SubtopicRecord[] | undefined,
  visibleSubtopicIds?: string[]
): Subtopic[] => {
  const existingIds = new Set(subtopicRecords?.map(record => record.id) ?? [])
  const visibleIds = visibleSubtopicIds
    ? new Set(visibleSubtopicIds)
    : undefined
  return topic.subtopics.filter(
    subtopic =>
      !existingIds.has(subtopic.id) &&
      (visibleIds === undefined || visibleIds.has(subtopic.id))
  )
}

export const formatSubtopicTitles = (subtopics: Subtopic[]): string => {
  const titles = subtopics.map(s => s.shortTitle ?? s.title)
  const head = titles.slice(0, 2).join(' / ')
  return titles.length > 2 ? `${head} / ...` : head
}

export const omitSubtopicAnswers = (
  topic: Topic,
  subtopicId: string,
  answers: SubmissionAnswers
): SubmissionAnswers => {
  const sections = pickSubtopicSections(topic, subtopicId)
  if (!sections) return answers
  const removeIds = new Set(sections.flatMap(s => s.questions).map(q => q.id))
  return Object.fromEntries(
    Object.entries(answers).filter(([id]) => !removeIds.has(id))
  )
}
